import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AssignmentPeriod } from '@core/models/assignment-period.model';
import { BaseComponent } from '@portal-shared/app/components/base-components/base.component';
import { KeyboardEnum } from '@portal-shared/app/enums/keyboard.enum';
import { AssignmentPeriodService } from '@services/assignment-period.service';
import { AssignmentDto } from '@services/dtos/assignment.dto';
import { isFinite } from 'lodash-es';

@Component({
    selector: 'al-assignment-period-cell-editor',
    templateUrl: './assignment-period-cell-editor.component.html',
    styleUrls: ['./assignment-period-cell-editor.component.scss'],
    imports: [FormsModule]
})
// eslint-disable-next-line brace-style
export class AssignmentPeriodCellEditorComponent
  extends BaseComponent
  implements ICellEditorAngularComp, AfterViewInit
{
  public readonly hoursPattern = AssignmentPeriodService.HOURS_PATTERN;

  public value = '';
  public highlightAllOnFocus = true;
  public cancelBeforeStart = false;

  //Keys Interaction configs.
  private static highlightAllOnFocusKeys = [KeyboardEnum.KEY_F2]; //Excel behaviour
  private static enterEditionModeKeys = [KeyboardEnum.KEY_F2, KeyboardEnum.KEY_ENTER, KeyboardEnum.KEY_TAB];
  private static clearCellOnInitKeys = [KeyboardEnum.KEY_BACKSPACE, KeyboardEnum.KEY_DELETE];
  private static finishingKeys = [KeyboardEnum.KEY_ENTER, KeyboardEnum.KEY_TAB];
  private static deleteKeys = [KeyboardEnum.KEY_DELETE, KeyboardEnum.KEY_BACKSPACE];
  private static moveKeys = [KeyboardEnum.KEY_ARROW_LEFT, KeyboardEnum.KEY_ARROW_RIGHT];

  @ViewChild('input', { read: ElementRef })
  public input!: ElementRef<HTMLInputElement>;

  public constructor() {
    super();
  }

  public agInit(params: ICellEditorParams<AssignmentDto, AssignmentPeriod>) {
    this.setInitialState(params);
  }

  public setInitialState(params: ICellEditorParams<AssignmentDto, AssignmentPeriod>) {
    let startValue: string | undefined;
    let highlightAllOnFocus = true;
    // only start edit if key pressed is a number, not a letter

    const isSpecialKey = params.eventKey
      ? Object.values(KeyboardEnum).includes(params.eventKey as KeyboardEnum)
      : false;

    this.cancelBeforeStart = !!params.eventKey && !isSpecialKey && !this.isNumber(params.eventKey);

    if (!AssignmentPeriodCellEditorComponent.clearCellOnInitKeys.includes(params.eventKey as KeyboardEnum)) {
      // if backspace or delete pressed, we leave startValue to ''
      if (
        params.eventKey &&
        !AssignmentPeriodCellEditorComponent.enterEditionModeKeys.includes(params.eventKey as KeyboardEnum)
      ) {
        // if a letter was pressed, we start with the letter
        startValue = params.eventKey ?? undefined;
        highlightAllOnFocus = false;
      } else {
        // otherwise we start with the current value
        startValue = params.value?.plannedHours?.toString();
        if (AssignmentPeriodCellEditorComponent.highlightAllOnFocusKeys.includes(params.eventKey as KeyboardEnum)) {
          highlightAllOnFocus = false;
        }
      }
    }
    this.value = startValue ?? '';
    this.highlightAllOnFocus = highlightAllOnFocus;
  }

  public ngAfterViewInit() {
    //run right now.
    window.setTimeout(() => {
      this.input.nativeElement.focus();
      if (this.highlightAllOnFocus) {
        this.input.nativeElement.select();

        this.highlightAllOnFocus = false;
      } else {
        // when we started editing, we want the caret at the end, not the start.
        // this comes into play in two scenarios:
        //   a) when user hits F2
        //   b) when user hits a printable character
        const value = this.input.nativeElement.value;
        const length = value ? value.length : 0;
        if (length > 0) {
          this.input.nativeElement.setSelectionRange(length, length);
        }
      }

      this.input.nativeElement.focus();
    });
  }

  private isNumber(charPress: string) {
    return isFinite(Number(charPress));
  }

  public getValue() {
    return this.value;
  }

  public isCancelBeforeStart(): boolean {
    return this.cancelBeforeStart;
  }

  public onKeyDown(event: KeyboardEvent) {
    if (this.isLeftOrRight(event) || this.isDeleteOrBackspace(event)) {
      event.stopPropagation();
      return;
    }

    if (!this.finishedEditingPressed(event) && !this.isValidNumeric(event)) {
      event.preventDefault();
    }
  }

  public onModelChange(value: string) {
    if (!value || this.hoursPattern.test(value)) {
      // update the model
      this.value = value;
    } else {
      // should happen only in certain conditions not caught by the onKeyDown method.
      // revert the HTML input value and reset its cursor/selection position
      const { selectionStart, selectionEnd } = this.input.nativeElement;
      this.input.nativeElement.value = this.value ?? '';
      this.input.nativeElement.setSelectionRange(selectionStart, selectionEnd);
    }
  }

  /**
   * Will check if the last inputted char helps
   * to build a number or if it creates a bad input
   * that we'll refuse. French commas for decimals are
   * replaced with dots in validation.
   * @param event Keyboard event.
   * @returns If the input is valid.
   */
  public isValidNumeric(event: KeyboardEvent): boolean {
    const input = event.target as HTMLInputElement;

    // Insert the pressed key at the cursor position/selection
    const value =
      input.value.slice(0, input.selectionStart ?? undefined) +
      event.key +
      input.value.slice(input.selectionEnd ?? input.value.length);

    return this.hoursPattern.test(value.trim());
  }

  private isDeleteOrBackspace(event: KeyboardEvent) {
    return AssignmentPeriodCellEditorComponent.deleteKeys.includes(event.key as KeyboardEnum);
  }

  private isLeftOrRight(event: KeyboardEvent) {
    return AssignmentPeriodCellEditorComponent.moveKeys.includes(event.key as KeyboardEnum);
  }

  private finishedEditingPressed(event: KeyboardEvent) {
    return AssignmentPeriodCellEditorComponent.finishingKeys.includes(event.key as KeyboardEnum);
  }
}
